/**
 * Play types and payout multipliers.
 * Twin of `functions/src/core/rules/payouts.ts` (lint:arch fingerprint).
 */
export const PLAY_TYPES = {
  FIJO: 'fijo',
  CORRIDO: 'corrido',
  PARLE: 'parle',
  CENTENA: 'centena',
} as const;

/** Default payout multipliers per play type. */
export const DEFAULT_MULTIPLIERS = {
  [PLAY_TYPES.FIJO]: 75,
  [PLAY_TYPES.CORRIDO]: 25,
  [PLAY_TYPES.PARLE]: 800,
  [PLAY_TYPES.CENTENA]: 500,
};

/** Maximum digits allowed per play type. */
export const NUMBER_LENGTH: Record<string, number> = {
  [PLAY_TYPES.FIJO]: 2,
  [PLAY_TYPES.CORRIDO]: 2,
  [PLAY_TYPES.PARLE]: 2,
  [PLAY_TYPES.CENTENA]: 3,
};

/**
 * Calculate potential payout for a play.
 */
export function calculatePayout(
  type: string,
  stake: number,
  customMultipliers: Record<string, number> = {},
): number {
  const mults = { ...DEFAULT_MULTIPLIERS, ...customMultipliers };
  return (mults[type as keyof typeof mults] ?? 0) * stake;
}

/**
 * Determine if a play won given the published results.
 * Centena: when `pick3` is known, require the full 3-digit match; otherwise
 * fall back to last-2 of the play against first prize (legacy manuals).
 */
export function didPlayWin(
  play: { type: string; number: string; number2?: string },
  result: { first: string; second: string; third: string; pick3?: string | null },
): boolean {
  const { first, second, third } = result;
  const winners = [first, second, third];

  switch (play.type) {
    case PLAY_TYPES.FIJO:
      return play.number === first;
    case PLAY_TYPES.CORRIDO:
      return winners.includes(play.number);
    case PLAY_TYPES.PARLE:
      return winners.includes(play.number) && winners.includes(play.number2 ?? '');
    case PLAY_TYPES.CENTENA: {
      const played = String(play.number ?? '').replace(/\D/g, '').padStart(3, '0').slice(-3);
      if (played.length !== 3) return false;
      const pick3 = result.pick3 != null
        ? String(result.pick3).replace(/\D/g, '').padStart(3, '0').slice(-3)
        : '';
      if (pick3.length === 3) return played === pick3;
      return Boolean(first && played.slice(-2) === first);
    }
    default:
      return false;
  }
}
